// Cleanup helpers for Bootstrap modals in a Blazor app.
// Blazor can navigate without a full page load while Bootstrap modal state (backdrop/body classes)
// may remain if the modal is removed or navigation happens during transitions.

(function () {
  function getModal() {
    const bs = window.bootstrap;
    return bs && bs.Modal;
  }

  function safeCall(fn) {
    try { fn(); } catch { /* ignore */ }
  }

  function hideOpenModals() {
    const Modal = getModal();

    // Attempt graceful hide via Bootstrap API if available.
    if (Modal && typeof Modal.getInstance === 'function') {
      document.querySelectorAll('.modal.show').forEach((el) => {
        safeCall(() => {
          const instance = Modal.getInstance(el) || Modal.getOrCreateInstance?.(el);
          instance?.hide?.();
        });
      });

      // Also dispose instances for modals that are in the DOM but no longer shown.
      document.querySelectorAll('.modal').forEach((el) => {
        safeCall(() => {
          const instance = Modal.getInstance(el);
          instance?.dispose?.();
        });
      });
    } else {
      // Fallback: best-effort hide via classes.
      document.querySelectorAll('.modal.show').forEach((el) => {
        safeCall(() => el.classList.remove('show'));
      });
    }
  }

  function cleanupModalArtifacts() {
    // If no modals are shown, ensure body/backdrop are reset.
    const anyShown = document.querySelector('.modal.show') != null;
    if (!anyShown) {
      safeCall(() => document.body.classList.remove('modal-open'));
      safeCall(() => { document.body.style.overflow = ''; });
      safeCall(() => { document.body.style.paddingRight = ''; });

      document.querySelectorAll('.modal-backdrop').forEach((b) => safeCall(() => b.remove()));
    }

    // Remove stray modal backdrops even if a modal remains but backdrop got duplicated.
    const backdrops = Array.from(document.querySelectorAll('.modal-backdrop'));
    if (backdrops.length > 1) {
      backdrops.slice(0, backdrops.length - 1).forEach((b) => safeCall(() => b.remove()));
    }
  }

  function hasOpenModalArtifacts() {
    try {
      return document.querySelector('.modal.show, .modal-backdrop') != null || document.body.classList.contains('modal-open');
    } catch {
      return false;
    }
  }

  async function closeAllModals() {
    // First pass: request hide via Bootstrap API.
    hideOpenModals();

    // Give Bootstrap a chance to run transition callbacks.
    await new Promise((r) => window.setTimeout(r, 50));

    // Second pass: in case a modal became visible during the first hide.
    hideOpenModals();

    // Let any queued callbacks run.
    await new Promise((r) => window.setTimeout(r, 0));

    cleanupModalArtifacts();

    // Final defensive cleanup in case the modal element was removed before Bootstrap finished.
    // If there are no shown modals, remove any lingering backdrops/body state.
    cleanupModalArtifacts();
  }

  function forceRemoveModalOverlays() {
    // Sometimes an old backdrop or a hidden modal element remains and intercepts pointer events.
    document.querySelectorAll('.modal-backdrop').forEach((b) => safeCall(() => b.remove()));

    document.querySelectorAll('.modal').forEach((m) => {
      safeCall(() => {
        // If it's not shown, it should not be blocking input.
        if (!m.classList.contains('show')) {
          m.style.display = 'none';
          m.setAttribute('aria-hidden', 'true');
          m.removeAttribute('aria-modal');
          m.removeAttribute('role');
        }
      });
    });

    safeCall(() => document.body.classList.remove('modal-open'));
    safeCall(() => { document.body.style.overflow = ''; });
    safeCall(() => { document.body.style.paddingRight = ''; });

    // Clear focus trap side-effects: if body still points to an element inside a removed modal,
    // move focus to a safe element.
    safeCall(() => {
      const active = document.activeElement;
      if (active && active.closest && active.closest('.modal')) {
        if (document.getElementById('main-content')) {
          document.getElementById('main-content').focus();
        } else {
          document.body.focus();
        }
      }
    });
  }

  async function closeAllModalsAfterNavigation() {
    // Wait a couple ticks so SPA frameworks (Blazor) can process the history navigation first.
    await new Promise((r) => window.setTimeout(r, 0));
    await new Promise((r) => window.setTimeout(r, 0));

    await closeAllModals();

    // Final hard reset to ensure nothing is intercepting input.
    forceRemoveModalOverlays();
  }

  window.bootstrapModalCleanup = {
    closeAllModals,
    closeAllModalsAfterNavigation,
    hideOpenModals,
    cleanupModalArtifacts,
    hasOpenModalArtifacts,
    forceRemoveModalOverlays
  };

  // Best-effort: cleanup on pagehide (covers navigation/unload)
  window.addEventListener('pagehide', () => {
    // Don't await.
    closeAllModals();
  });
})();
